/**
 * Eén afspeelwachtrij voor alles wat de tablet laat klinken: chime, clip, toestelstem en de
 * videofoonaankondiging.
 *
 * Spec: specs/20261020-spraakmeldingen-elevenlabs/spec.md §5.7, FR-0.1
 * Plan: fase 0
 * Taak: T043 (tests: services/__tests__/AudioQueue.test.ts, T038)
 *
 * ── WAAROM ER MAAR ÉÉN IS ───────────────────────────────────────────────────
 *
 * Op een handsfree wandtablet komt alles uit dezelfde luidspreker. `presentAndSound()`
 * startte de chime en de stem tegelijk (`void speakFor()` naast `void playFor()`), en bij de
 * deurbelflow met `soundRepeat: 2` klonk de chime twee keer ÓVER de gesproken zin. Twee
 * geluiden tegelijk zijn geen "wat rommelig": het zijn twee onverstaanbare geluiden.
 *
 * ── DE REGELS ───────────────────────────────────────────────────────────────
 *
 *   1. Een REEKS (één melding, één aankondiging) speelt zijn items na elkaar.
 *   2. Een reeks met een HOGERE prioriteit onderbreekt: `stop()` op het lopende item, de rest
 *      van de onderbroken reeks vervalt (`skipped`) en komt niet terug. Een oude melding na
 *      de deurbel herhalen vertelt de bewoner iets wat niet meer klopt.
 *   3. GELIJKE of lagere prioriteit wacht. Een wachtende hogere gaat vóór een wachtende lagere.
 *   4. Tijdens een GESPREK speelt er niets (`muted`), ook geen alarm: de melding blijft
 *      zichtbaar, de ack zegt `muted_call`. Wat gedempt werd, komt na het gesprek niet terug.
 *   5. Elk item heeft een TIME-OUT (duur + 3 s, of 15 s als de duur onbekend is). Een speler
 *      die nooit `ended` meldt — en die bestaan op Chromium 69–84 — blokkeert de wachtrij dus
 *      hoogstens één time-out lang.
 *
 * ── WAT HIER BEWUST NIET IN STAAT ───────────────────────────────────────────
 *
 * Geen `AudioContext`, geen `new Audio()`, geen native brug: deze module plant alleen. Hoe een
 * item klinkt, beslist het item zelf (`services/audioItems.ts`). ⚠ Maak nooit een
 * `AudioContext` per geluid aan — op 2026-10-23 crashte de tablet daarop na ~32 s aftellen;
 * wie Web Audio nodig heeft, gebruikt `getUnlockedAudioContext()` uit `@caro/shared/sip`.
 *
 * Ook de beltoonlus van de videofoon staat er niet in: een lus heeft geen einde, en in de
 * wachtrij zou hij elk ander geluid voor altijd tegenhouden.
 *
 * Chromium 84 is de ondergrens: geen `AbortSignal.timeout`, geen `structuredClone`.
 */

export type AudioItemKind = 'chime' | 'clip' | 'tts' | 'file';

/** Hoog naar laag: alarm > videofoon > deurbel > de rest. */
export type AudioPriority = 'alarm' | 'videophone' | 'doorbell' | 'default';

export const AUDIO_PRIORITY_RANK: Record<AudioPriority, number> = {
  alarm: 3,
  videophone: 2,
  doorbell: 1,
  default: 0,
};

export interface AudioItem {
  kind: AudioItemKind;
  /** Verwachte duur in ms. Bepaalt de time-out (duur + 3 s); onbekend = 15 s. */
  durationMs?: number;
  /**
   * Speel af. Resolve't wanneer het geluid ÉÉNDIGT, rejecte bij een fout. Een `undefined`
   * terug (oude WebView) telt als "meteen klaar".
   */
  play(): Promise<unknown> | unknown;
  /** Stop meteen. Mag niet gooien; doet het toch, dan wordt dat genegeerd. */
  stop(): void;
}

/**
 * Hoe een item afliep.
 *
 *   played       — speelde tot het einde
 *   failed       — `play()` gooide of rejecte
 *   timeout      — geen einde binnen de time-out; `stop()` is aangeroepen
 *   interrupted  — liep en werd gestopt (hogere prioriteit, `cancel()`, `stopAll()`)
 *   skipped      — kwam niet aan de beurt omdat zijn reeks onderbroken werd
 *   muted        — een gesprek liep; niet (verder) gespeeld
 */
export type AudioItemOutcome = 'played' | 'failed' | 'timeout' | 'interrupted' | 'skipped' | 'muted';

export interface EnqueueOptions {
  priority?: AudioPriority;
  /** Vlak vóór `play()` van item `index`. */
  onItemStart?: (index: number, item: AudioItem) => void;
  /** Voor ELK item, ook wanneer het niet speelde (`skipped`/`muted`). */
  onItemEnd?: (index: number, outcome: AudioItemOutcome, item: AudioItem) => void;
}

export interface AudioSequenceHandle {
  /** De uitkomst per item, in volgorde. Rejecte nooit. */
  done: Promise<AudioItemOutcome[]>;
  /** Stop deze reeks (lopend item `interrupted`, de rest `skipped`). Idempotent. */
  cancel(): void;
}

export const DEFAULT_ITEM_TIMEOUT_MS = 15_000;
export const ITEM_TIMEOUT_MARGIN_MS = 3_000;

/** Duur + 3 s, of 15 s als de duur onbekend is. */
export function itemTimeoutMs(item: AudioItem): number {
  const d = item && typeof item.durationMs === 'number' && item.durationMs > 0 ? item.durationMs : 0;
  return d > 0 ? d + ITEM_TIMEOUT_MARGIN_MS : DEFAULT_ITEM_TIMEOUT_MS;
}

// ── De reeks voor één melding ──────────────────────────────────────────────

export interface PlanOptions {
  /** Maakt één chime-item; wordt `repeat` keer aangeroepen. */
  chime?: (() => AudioItem) | null;
  /** `soundRepeat` uit het flowblok; begrensd op 1..10, net als in de API. */
  repeat?: number;
  /** De toestelstem (Android-TTS / Web Speech). */
  voice?: AudioItem | null;
  /**
   * Een vooraf gerenderde clip (fase 1+). `mixed: true` = het flowgeluid zit al in de clip,
   * dus dan speelt ALLEEN de clip. Anders vervangt de clip de toestelstem, na de chimes.
   */
  clip?: { item: AudioItem; mixed: boolean } | null;
}

/** Chime × `repeat` → stem (of clip). Een `mixed`-clip alleen. */
export function planSequence(opts: PlanOptions): AudioItem[] {
  if (opts.clip && opts.clip.mixed) return [opts.clip.item];
  const items: AudioItem[] = [];
  if (opts.chime) {
    const n = Math.max(1, Math.min(10, Math.floor(Number(opts.repeat) || 1)));
    for (let i = 0; i < n; i += 1) items.push(opts.chime());
  }
  const spoken = opts.clip ? opts.clip.item : opts.voice;
  if (spoken) items.push(spoken);
  return items;
}

// ── De wachtrij ────────────────────────────────────────────────────────────

interface Sequence {
  seq: number;
  items: AudioItem[];
  rank: number;
  opts: EnqueueOptions;
  outcomes: AudioItemOutcome[];
  /** Waarom de reeks stopt, zodra ze stopt. */
  stopReason: 'interrupted' | 'muted' | null;
  /** Breekt het lopende item af met deze uitkomst. */
  abort: ((outcome: AudioItemOutcome) => void) | null;
  finished: boolean;
  resolve: (outcomes: AudioItemOutcome[]) => void;
}

let current: Sequence | null = null;
let waiting: Sequence[] = [];
let callActive = false;
let seqCounter = 0;

function safe(fn: () => void) {
  try {
    fn();
  } catch {
    /* een luisteraar of een speler die gooit, mag de wachtrij niet slopen */
  }
}

function safeStop(item: AudioItem) {
  safe(() => item.stop());
}

/** Rond een reeks af: wat nog niet gespeeld is krijgt `fill`, dan `done`. */
function finish(s: Sequence, fill: AudioItemOutcome) {
  if (s.finished) return;
  s.finished = true;
  for (let i = s.outcomes.length; i < s.items.length; i += 1) {
    s.outcomes.push(fill);
    const item = s.items[i];
    if (s.opts.onItemEnd) safe(() => s.opts.onItemEnd!(i, fill, item));
  }
  s.resolve(s.outcomes.slice(0));
}

/**
 * Speel één item. De uitkomst wordt HIER vastgelegd (in `settle`), niet door de aanroeper:
 * zo telt een item dat net klaar was op het moment dat iemand onderbreekt, nog als `played`
 * in plaats van als `skipped`.
 */
function runItem(s: Sequence, index: number, item: AudioItem): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = (outcome: AudioItemOutcome) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      s.abort = null;
      if (!s.finished) {
        s.outcomes.push(outcome);
        if (s.opts.onItemEnd) safe(() => s.opts.onItemEnd!(index, outcome, item));
      }
      resolve();
    };
    s.abort = (outcome) => {
      safeStop(item);
      settle(outcome);
    };
    timer = setTimeout(() => {
      safeStop(item);
      settle('timeout');
    }, itemTimeoutMs(item));

    let result: unknown;
    try {
      result = item.play();
    } catch {
      settle('failed');
      return;
    }
    // ⚠ `Promise.resolve`, geen `.then` op het resultaat: `play()` kan op een oude WebView
    // `undefined` teruggeven, en dan bestaat `.then` niet.
    Promise.resolve(result).then(
      () => settle('played'),
      () => settle('failed'),
    );
  });
}

async function run(s: Sequence) {
  for (let i = 0; i < s.items.length; i += 1) {
    if (s.finished || s.stopReason) break;
    const item = s.items[i];
    if (callActive) {
      s.stopReason = 'muted';
      break;
    }
    if (s.opts.onItemStart) safe(() => s.opts.onItemStart!(i, item));
    // eslint-disable-next-line no-await-in-loop
    await runItem(s, i, item);
    if (s.finished) return;
  }
  const fill: AudioItemOutcome = s.stopReason === 'muted' ? 'muted' : 'skipped';
  finish(s, fill);
  if (current === s) {
    current = null;
    pump();
  }
}

function pump() {
  if (current || waiting.length === 0) return;
  const next = waiting.shift()!;
  if (callActive) {
    // Kan niet voorkomen via `enqueue` (die dempt meteen), wel als het gesprek begon terwijl
    // deze reeks wachtte en `setCallActive` hem nog niet zag.
    finish(next, 'muted');
    pump();
    return;
  }
  current = next;
  void run(next);
}

/**
 * Stop een reeks met deze reden. Het lopende item krijgt `outcome`, de rest `fill`.
 *
 * Synchroon: wie onderbreekt, mag meteen beginnen — het onderbroken item rondt zijn eigen
 * promiseketen later af, maar raakt `current` dan niet meer aan.
 */
function stopSequence(s: Sequence, outcome: AudioItemOutcome, fill: AudioItemOutcome) {
  if (s.finished) return;
  s.stopReason = fill === 'muted' ? 'muted' : 'interrupted';
  // Het lopende item legt zijn eigen uitkomst vast (zie `runItem`).
  if (s.abort) s.abort(outcome);
  finish(s, fill);
  if (current === s) current = null;
  waiting = waiting.filter((w) => w !== s);
}

function insertWaiting(s: Sequence) {
  // Stabiel: hoger eerst, en binnen dezelfde rang wie eerst kwam.
  let at = waiting.length;
  for (let i = 0; i < waiting.length; i += 1) {
    if (waiting[i].rank < s.rank) {
      at = i;
      break;
    }
  }
  waiting.splice(at, 0, s);
}

/** Zet een reeks in de wachtrij. Werpt nooit. */
export function enqueue(items: AudioItem[], opts: EnqueueOptions = {}): AudioSequenceHandle {
  const list = (items || []).filter(Boolean);
  let resolveDone: (o: AudioItemOutcome[]) => void = () => {};
  const done = new Promise<AudioItemOutcome[]>((r) => {
    resolveDone = r;
  });
  const s: Sequence = {
    seq: (seqCounter += 1),
    items: list,
    rank: AUDIO_PRIORITY_RANK[opts.priority || 'default'] ?? 0,
    opts,
    outcomes: [],
    stopReason: null,
    abort: null,
    finished: false,
    resolve: resolveDone,
  };
  const handle: AudioSequenceHandle = {
    done,
    cancel: () => {
      if (s.finished) return;
      const wasCurrent = current === s;
      stopSequence(s, 'interrupted', 'skipped');
      if (wasCurrent) pump();
    },
  };

  if (list.length === 0) {
    finish(s, 'skipped');
    return handle;
  }
  if (callActive) {
    // Alleen visueel. De ack zegt `muted_call`.
    finish(s, 'muted');
    return handle;
  }

  if (current && s.rank > current.rank) {
    stopSequence(current, 'interrupted', 'skipped');
    current = s;
    void run(s);
    return handle;
  }
  insertWaiting(s);
  pump();
  return handle;
}

/**
 * Er loopt een gesprek (of niet meer). `true` stopt wat speelt en dempt alles wat wacht en
 * alles wat er nog bij komt. `false` speelt niets opnieuw af: wat gedempt werd, is voorbij.
 */
export function setCallActive(active: boolean): void {
  const next = !!active;
  if (next === callActive) return;
  callActive = next;
  if (!callActive) return;
  const all = (current ? [current] : []).concat(waiting);
  current = null;
  waiting = [];
  for (const s of all) stopSequence(s, 'muted', 'muted');
}

export function isCallActive(): boolean {
  return callActive;
}

/** Stop alles wat speelt en wacht (`interrupted` / `skipped`). */
export function stopAll(): void {
  const all = (current ? [current] : []).concat(waiting);
  current = null;
  waiting = [];
  for (const s of all) stopSequence(s, 'interrupted', 'skipped');
}

/** Speelt er nu iets? Voor de diagnose-overlay en de tests. */
export function isBusy(): boolean {
  return current !== null;
}

// ── De terugvalladder van één gesproken item (spraakmeldingen fase 1, T091) ────
//
// Spec §5.7 "Terugvalladder": clip uit de VoiceCache (native, dan WebView — dat kiest het
// `fileItem` zelf) → Android-TTS met dezelfde tekst → het oude geluid → niets. De ladder is
// ÉÉN wachtrij-item: wordt hij onderbroken, dan stopt de trede die speelt en komt er geen
// volgende. Een trede die niet beschikbaar is (`make()` geeft `null`) of faalt, geeft de beurt
// aan de volgende. Er speelt nooit meer dan één trede tegelijk.

/** Waar de klank van een trede vandaan komt; bepaalt `voiceSource` in de ack. */
export type LadderSource = 'render' | 'native' | 'fallback';

export interface LadderStep {
  source: LadderSource;
  /** Bouw het item van deze trede, of `null` als hij er niet is (bv. geen clip in de cache). */
  make: () => AudioItem | null | undefined | Promise<AudioItem | null | undefined>;
}

export interface LadderAttempt {
  source: LadderSource;
  outcome: 'unavailable' | 'failed' | 'played';
}

export interface LadderResult {
  /** De trede die tot het einde speelde, of `null`. */
  played: LadderSource | null;
  attempts: LadderAttempt[];
}

export function ladderItem(
  steps: LadderStep[],
  opts: { kind?: AudioItemKind; durationMs?: number } = {},
): AudioItem & { result: LadderResult } {
  const result: LadderResult = { played: null, attempts: [] };
  let current: AudioItem | null = null;
  let stopped = false;
  return {
    kind: opts.kind || 'clip',
    durationMs: opts.durationMs,
    result,
    async play() {
      stopped = false;
      result.played = null;
      result.attempts = [];
      for (const step of steps) {
        if (stopped) return;
        let item: AudioItem | null | undefined = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          item = await step.make();
        } catch {
          item = null;
        }
        if (stopped) return;
        if (!item) {
          result.attempts.push({ source: step.source, outcome: 'unavailable' });
          continue;
        }
        current = item;
        try {
          // ⚠ `Promise.resolve`: `play()` kan op een oude WebView `undefined` teruggeven.
          // eslint-disable-next-line no-await-in-loop
          await Promise.resolve(item.play());
          current = null;
          if (stopped) return;
          result.attempts.push({ source: step.source, outcome: 'played' });
          result.played = step.source;
          return;
        } catch {
          current = null;
          if (stopped) return;
          result.attempts.push({ source: step.source, outcome: 'failed' });
        }
      }
      throw new Error('ladder_exhausted');
    },
    stop() {
      stopped = true;
      const c = current;
      current = null;
      if (c) safeStop(c);
    },
  };
}

/** Speel items na elkaar als één item. Alleen het LAATSTE bepaalt of het geslaagd is. */
export function chainItem(items: AudioItem[], kind: AudioItemKind = 'clip'): AudioItem {
  let current: AudioItem | null = null;
  let stopped = false;
  const total = items.reduce((n, i) => n + (typeof i.durationMs === 'number' ? i.durationMs : 0), 0);
  return {
    kind,
    durationMs: total > 0 ? total : undefined,
    async play() {
      stopped = false;
      for (let i = 0; i < items.length; i += 1) {
        if (stopped) return;
        current = items[i];
        const last = i === items.length - 1;
        try {
          // eslint-disable-next-line no-await-in-loop
          await Promise.resolve(items[i].play());
        } catch (err) {
          if (last) throw err;
        } finally {
          current = null;
        }
      }
    },
    stop() {
      stopped = true;
      const c = current;
      current = null;
      if (c) safeStop(c);
    },
  };
}

export type LadderVoiceSource = 'render' | 'native' | 'none';
export type LadderVoiceReason = 'cache_miss' | 'play_failed' | 'muted_call';

/**
 * Van de ladder naar de ack-velden (FR-0.4). `render` = de clip, `native` = Android-TTS. Een
 * oud geluid is GEEN stem: `voicePlayed: false`, met de EERSTE oorzaak — `cache_miss` als er
 * geen clip te vinden was, `play_failed` als hij er was maar niet speelde.
 */
export function ladderVoiceAck(
  result: LadderResult,
  outcome: AudioItemOutcome,
): { voicePlayed: boolean; voiceSource: LadderVoiceSource; voiceReason?: LadderVoiceReason } {
  if (outcome === 'muted') return { voicePlayed: false, voiceSource: 'none', voiceReason: 'muted_call' };
  if (result.played === 'render') return { voicePlayed: true, voiceSource: 'render' };
  if (result.played === 'native') return { voicePlayed: true, voiceSource: 'native' };
  const first = result.attempts[0];
  if (first && first.source === 'render') {
    return {
      voicePlayed: false,
      voiceSource: 'none',
      voiceReason: first.outcome === 'unavailable' ? 'cache_miss' : 'play_failed',
    };
  }
  if (outcome === 'failed' || outcome === 'timeout') {
    return { voicePlayed: false, voiceSource: 'none', voiceReason: 'play_failed' };
  }
  // Onderbroken vóór er een trede klaar was: geen volledige zin, geen reden uit de enum.
  return { voicePlayed: false, voiceSource: 'none' };
}

/** Alleen voor tests. */
export function __reset(): void {
  stopAll();
  callActive = false;
  seqCounter = 0;
}
